/**
 * Admin: airport house names (SA-1054, design note
 * docs/superpowers/specs/2026-10-09-sa1054-airport-house-names-admin-page-design.md).
 * The operator's name and city for a station, written over the source's words
 * through PATCH /api/airports/{ident}. The page prints the server's
 * `displayName` and `placeWords` and computes no wording of its own.
 */
import { useState } from 'react'
import { Alert, AlertDescription, Badge, Button, Input, Spinner } from '@/components/ui'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useAirportAdminSearch, useHouseAirports } from '@/hooks/useAirportHouse'
import { SEARCH_MIN } from '@/hooks/useAirports'
import type { Airport } from '@/types/airports'
import { AirportHouseDialog } from './AirportHouseDialog'
import * as text from './airportsText'

type OnEdit = (airport: Airport) => void

function StationRow({ airport, onEdit }: { airport: Airport; onEdit: OnEdit }) {
  const code = text.codeOf(airport)
  return (
    <li className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
      <div className="min-w-0 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-semibold">{code}</span>
          <span className="font-medium text-foreground">{airport.displayName}</span>
          {airport.retired && <Badge variant="muted-soft">{text.RETIRED}</Badge>}
        </div>
        {airport.placeWords && <p className="text-sm text-muted-foreground">{airport.placeWords}</p>}
        <p className="text-xs text-muted-foreground">{text.sourceLine(airport)}</p>
      </div>
      <Button variant="outline" size="sm" aria-label={`Edit ${code}`} onClick={() => onEdit(airport)}>
        Edit
      </Button>
    </li>
  )
}

function StationList({ label, rows, onEdit }: { label: string; rows: Airport[]; onEdit: OnEdit }) {
  return (
    <ul aria-label={label} className="space-y-2">
      {rows.map((a) => <StationRow key={a.ident} airport={a} onEdit={onEdit} />)}
    </ul>
  )
}

function SearchResults({ q, onEdit }: { q: string; onEdit: OnEdit }) {
  const search = useAirportAdminSearch(q)
  const typed = q.trim()
  if (typed.length < SEARCH_MIN) return null
  const muted = 'text-sm text-muted-foreground'
  // A failed background refetch keeps the last rows (the readUnavailable rule).
  if (search.asked !== typed || (search.data === undefined && !search.isError)) {
    return <p role="status" className={muted}>{text.SEARCHING}</p>
  }
  if (search.data === undefined) return <p className={muted}>{text.SEARCH_FAILED}</p>
  if (search.data.length === 0) return <p className={muted}>{text.noMatch(typed)}</p>
  return <StationList label={text.SEARCH_RESULTS} rows={search.data} onEdit={onEdit} />
}

function HouseList({ onEdit }: { onEdit: OnEdit }) {
  const house = useHouseAirports()
  const rows = house.data
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-foreground">
        {rows === undefined ? text.HOUSE_HEADING : text.houseHeading(rows.length)}
      </h2>
      {rows === undefined ? (
        house.isError ? (
          <Alert variant="destructive"><AlertDescription>{text.HOUSE_UNAVAILABLE}</AlertDescription></Alert>
        ) : (
          <div className="flex justify-center py-6"><Spinner /></div>
        )
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{text.HOUSE_EMPTY}</p>
      ) : (
        <StationList label={text.HOUSE_HEADING} rows={rows} onEdit={onEdit} />
      )}
    </section>
  )
}

export function AirportsPage() {
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState<Airport | null>(null)
  const ident = editing?.ident
  return (
    <PageFrame width="detail" className="space-y-6">
      <PageHeader title={text.PAGE_TITLE} subtitle={text.PAGE_SENTENCE} />
      <section className="space-y-3">
        <Input
          type="search" label={text.SEARCH_LABEL} placeholder={text.SEARCH_PLACEHOLDER}
          value={q} onChange={(e) => setQ(e.target.value)}
        />
        <SearchResults q={q} onEdit={setEditing} />
      </section>
      <HouseList onEdit={setEditing} />
      {/* A dialog closes only the station it was opened for: a save that lands after
          Cancel must never close the next station's dialog and drop its typing. */}
      <AirportHouseDialog
        key={ident ?? 'none'} airport={editing}
        onClose={() => setEditing((cur) => (cur?.ident === ident ? null : cur))}
      />
    </PageFrame>
  )
}
